<template>
  <div
    :class="{ 'm-t-lg': !openAttentionCases, 'm-t-md': openAttentionCases }"
    class="container"
  >
    <div class="columns">
      <div class="column">
        <div class="content">
          <h3>{{ $wording.home.title_1 }}</h3>
          <div class="notification is-light is-info">
            <p>
              Use this site to complete required lead disclosures and certifications for rental properties and family daycare facilities. Learn more about the <a
                href="https://www.phila.gov/programs/lead-and-healthy-homes-program/"
                target="_blank"
              >Lead and Healthy Homes Program <i class="fas fa-external-link" /></a> and property owners' certification requirements on our website.
            </p>
            <p>To begin:</p>
            <ul>
              <li>
                Search for your property below to start a lead disclosure process. This will determine if the property is subject to additional <a
                  href="https://www.phila.gov/services/permits-violations-licenses/apply-for-a-permit/environmental-permits-and-approvals/submit-lead-certification-and-inspection-reports/"
                  target="_blank"
                >certification requirements <i class="fas fa-external-link" /></a>. You can save these results and return to complete additional requirements later.
              </li>
              <li>
                If your property is required to be certified <a
                  href="/faq#link-1"
                  target="_blank"
                >lead safe or lead free</a>, you are required to have your property inspected by a certified professional licensed to operate in Pennsylvania.  Learn more about <a
                  href="https://www.phila.gov/documents/lead-and-healthy-homes-resources-for-landlords/"
                  target="_blank"
                >inspection requirements <i class="fas fa-external-link" /></a> and <a
                  href="https://www.phila.gov/services/permits-violations-licenses/apply-for-a-permit/environmental-permits-and-approvals/submit-lead-certification-and-inspection-reports/"
                  target="_blank"
                >finding a qualified lead inspector <i class="fas fa-external-link" /></a> on our website.  When you receive your results from the technician, use this site to submit proof of certification to the City.
              </li>
            </ul>
          </div>
        </div>


        <div
          v-if="$store.getters['users/unfinished']"
          class="has-text-centered"
        >
          <router-link
            tag="button"
            class="button is-primary"
            :to="{ name: 'profile' }"
          >
            Update profile.
          </router-link>
        </div>
        <template v-else>
          <div class="new-disclosure-panel content">
            <h4>Start a New Lead Disclosure</h4>
            <p>
              Search for a residential or family daycare property using the street address or <help-icon modal="opa-number">
                OPA number
              </help-icon> to start a lead disclosure process for that address.
            </p>
            <search-property
              ref="search-property"
              class="m-t-md"
              @selected-property="initEligibilityCheck"
            />
          </div>

          <div class="m-t-lg content">
            <h3 v-text="$wording.home.title_4" />
            <p class="lead-disclosures-description">
              The list below includes all lead certifications initiated for your properties.
            </p>

            <div class="lead-disclosures-search">
              <form @submit.prevent="triggerSearch">
                <label
                  for="search-control"
                  class="label has-text-weight-bold"
                >
                  Filter by address or OPA number:
                </label>
                <div class="field">
                  <div class="control">
                    <search
                      id="search-control"
                      v-model="search"
                      :attrs="{ input: { placeholder: 'Search by address...', id: 'search-control' } }"
                      :doing-search="doingSearch"
                      @clear="clearSearchTable"
                      @search="triggerSearch"
                    />
                  </div>
                </div>
              </form>
            </div>

            <div
              class="results-table-wrapper"
              :class="{ 'hide-edges': hideEdges }"
              @click.capture="handleTableClick"
            >
              <div
                v-if="doingSearch"
                class="table-loading-overlay"
                aria-live="polite"
              >
                <div class="searching-row">
                  Searching results...
                </div>
              </div>

              <v-server-table
                ref="dashboard-table"
                :columns="tableColumns"
                :options="tableOptions"
                @loaded="loaded"
              >
                <template
                  slot="submittedAt"
                  slot-scope="props"
                >
                  {{
                    $dayjs
                      .utc(props.row.submittedAt)
                      .local()
                      .format(tableOptions.dateFormat)
                  }}
                </template>

                <template
                  slot="certificationStatus"
                  slot-scope="props"
                >
                  <span
                    v-if="props.row.certificationStatus==='Passed'"
                    class="tag has-text-dark is-success"
                  >
                    Certification finished
                  </span>
                  <span
                    v-else-if="props.row.certificationStatus==='Pending'"
                    class="tag has-text-dark is-info"
                  >
                    Pending for review
                  </span>
                  <span
                    v-else-if="props.row.certificationStatus==='Draft'"
                    class="tag"
                  >
                    Certification not finished
                  </span>
                  <span
                    v-else
                    class="tag is-danger has-text-weight-bold"
                  >
                    Action required!
                  </span>
                </template>
                <template
                  slot="actions"
                  slot-scope="props"
                >
                  <button
                    class="button is-small is-secondary"
                    @click="initEligibilityDashboard(props.row)"
                  >
                    <span>Open</span>
                  </button>
                  <button
                    v-if="props.row.certificationStatus==='Draft'"
                    class="button is-small is-outlined is-danger m-l-sm"
                    title="Remove lead disclosure"
                    @click="withdrawCertification(props.row)"
                  >
                    <span
                      class="icon"
                      aria-hidden="true"
                    >
                      <i class="fas fa-trash" />
                    </span>
                  </button>
                </template>
              </v-server-table>
            </div>
          </div>
        </template>
      </div>
    </div>
    <more-info
      id="opa-number"
      :text="$wording.home.opa_number"
      :title="$wording.home.opa_number_title"
    />
  </div>
</template>

<script>
import Vue from 'vue';
import { ServerTable } from 'vue-tables-2';

import Search from '@/components/common/Search';
import SearchProperty from '@/components/SearchProperty';
import MoreInfo from '@/components/modals/MoreInfo';
import HelpIcon from '@/components/common/HelpIcon';
import { options } from '@/config/tableOptions';
import Searches from '@/mixins/searches';

Vue.use(ServerTable, {}, false, 'bulma');

export default {
  name: "SelectProperty",
  components: {
    Search,
    SearchProperty,
    MoreInfo,
    HelpIcon,
  },
  mixins: [ Searches ],
  data() {
    return {
      searchAction: 'properties/searchSubmissions',
      allowedRoutes: [ 'home' ],
      returnFromSearchParam: 'eligibilityChecks',
      searchTypes: { opaAccountNumber: 'Address' },
      limit: 15,
      orderBy: 'submittedAt',
      tableOptions: options({
        requestFunction: this.requestFunction,
        responseAdapter: this.responseAdapter,
        perPage: 15,
        dateColumns: [ 'submittedAt' ],
        sortable: [ 'submittedAt' ],
        orderBy: {
          ascending: false,
          column: 'submittedAt',
        },
        filterable: [],
        resizableColumns: true,
        columnsClasses: {
          status: 'is-status nowrap has-text-centered',
          submittedAt: 'nowrap',
          actions: 'has-text-left actions p-sm',
        },
      }),
      tableColumns: [
        'opaAccountNumber',
        'opaAddress',
        'submittedAt',
        'propertyType',
        'certificationStatus',
        'actions',
      ],
      skipNextLoadedSearchReset: false,
    };
  },
  methods: {
    handleTableClick(event) {
      const target = event && event.target;
      if (!target || typeof target.closest !== 'function') {
        return;
      }

      const paginationControl = target.closest('.VuePagination a, .VuePagination button');
      if (!paginationControl) {
        return;
      }

      const parentLi = paginationControl.closest('li');
      const isDisabled =
        paginationControl.classList.contains('disabled') ||
        paginationControl.getAttribute('aria-disabled') === 'true' ||
        (parentLi && parentLi.classList.contains('disabled'));

      if (!isDisabled) {
        this.doingSearch = true;
      }
    },

    async waitForLoadingPaint() {
      await this.$nextTick();
      await new Promise((resolve) => {
        if (typeof window !== 'undefined' && window.requestAnimationFrame) {
          window.requestAnimationFrame(() => resolve());
          return;
        }

        setTimeout(resolve, 0);
      });
    },

    responseAdapter(data) {
      const resp = { data: [], count: 0 };
      if (typeof data === "object" && data.totalCount !== undefined) {
        resp.data = data[this.returnFromSearchParam] || [];
        resp.count = data.totalCount || 0;

        if (data.pageNumber > data.totalPages) {
          resp.data = [];
          resp.count = 0;
          this.page = 1;
          this.triggerSearch();
        }
      }
      return resp;
    },

    loaded(data) {
      if (this.skipNextLoadedSearchReset) {
        this.skipNextLoadedSearchReset = false;
      } else {
        this.doingSearch = false;
      }

      this.totalCount = data.totalCount;
    },

    async requestFunction(params) {
      if (this.requestMadeByAnUser === undefined || this.requestMadeByAnUser === true) {
        this.doingSearch = true;

        let query = this.$route.query;
        query.ascending = query.ascending === 'true';

        const pageNumber = parseInt(query.pageNumber || params.page || this.page || 1);
        const pageSize = parseInt(query.pageSize || params.limit || this.limit || 15);

        let requestObject = {
          ...params,
          ...query,
          pageNumber,
          pageSize,
          orderByCol: query.orderByCol || params.orderBy,
          type: 'opaAccountNumber',
          status: undefined,
          propertyType: undefined,
          zipCode: undefined,
          startDate: undefined,
          endDate: undefined,
          caseType: undefined,
        };

        this.requestMadeByAnUser = false;
        this.lastLoadedData = await this.$store.dispatch(this.searchAction, requestObject);
        return this.lastLoadedData;
      }

      this.requestMadeByAnUser = false;
      this.skipNextLoadedSearchReset = this.doingSearch;
      return this.lastLoadedData;
    },

    async triggerSearch() {
      this.doingSearch = true;
      await this.waitForLoadingPaint();

      this.search.type = 'opaAccountNumber';
      this.customObject = {
        pageNumber: parseInt(this.page),
        pageSize: parseInt(this.limit),
        orderByCol: this.orderBy,
        ascending: this.ascending ? true : false,
        type: 'opaAccountNumber',
        search: !this.search.search ? undefined : this.search.search,
        opaAccountNumber: undefined,
      };

      if (this.search.search) {
        try {
          this.customObject.opaAccountNumber = await this.$store.dispatch(
            'properties/getOPAAccountNumbers',
            this.search.search
          );
        } catch (error) {
          this.doingSearch = false;
          throw error;
        }
      }

      this.requestMadeByAnUser = true;
      this.$router.push({ query: this.customObject }).catch(() => {
        this.doingSearch = false;
      });
    },

    async hasExistingSubmission(opaAccountNumber) {
      const response = await this.$store.dispatch('properties/searchSubmissions', {
        opaAccountNumber: [ opaAccountNumber ],
        pageNumber: 1,
        pageSize: 1,
      });
      return !!(response && response.totalCount > 0);
    },

    async initEligibilityCheck(property) {
      const hasExistingSubmission = await this.hasExistingSubmission(property.opa_account_num);

      if (hasExistingSubmission) {
        this.$modal.show('custom-dialog', {
          title: this.$wording.home.duplicated_property_found,
          text: this.$wording.home.text_9,
          buttons: [
            {
              title: 'Yes',
              class: 'yes-button',
              handler: () => {
                this.$modal.hide('custom-dialog');
                this.$sCache.set('selectedOPAAccountNumber', property.opa_account_num);
                this.$router.push({ path: `/submissions/new` });
              },
            },
            {
              title: 'NO',
              default: true,
              class: 'is-dark no-button',
              handler: () => {
                this.$modal.hide('custom-dialog');
              },
            },
          ],
        });
      } else {
        this.$modal.hide('custom-dialog');
        this.$sCache.set('selectedOPAAccountNumber', property.opa_account_num);
        this.$router.push({ path: `/submissions/new` });
      }
    },

    initEligibilityDashboard(eligibility) {
      this.$sCache.set('selectedOPAAccountNumber', eligibility.opaAccountNumber);
      this.$router.push({ name: `submissions-details`, params: { uuid: eligibility.id }});
    },

    async withdrawCertification(row) {
      if (await this.submitGuard('Are you sure you would like to withdraw the selected certification?')) {
        if (await this.$store.dispatch('properties/deleteSubmission', row.id)) {
          this.$refs['dashboard-table'].getData();
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.new-disclosure-panel {
  border: 1px solid #d7dce2;
  border-radius: 0;
  padding: 1rem 1.25rem;
  background-color: #ffffff;
}

.lead-disclosures-description {
  margin-top: -0.25rem;
  margin-bottom: 1rem;
}

.lead-disclosures-search {
  margin-bottom: 1rem;
}

.results-table-wrapper {
  position: relative;
  min-height: 160px;
}

.table-loading-overlay {
  position: absolute;
  inset: 0;
  z-index: 2;
  background: rgba(255, 255, 255, 0.95);
  display: flex;
  align-items: flex-start;
}

.searching-row {
  width: 100%;
  border: 1px solid #d7dce2;
  background-color: #ffffff;
  padding: 0.75rem 1rem;
  font-weight: 600;
  color: #2f3f4f;
}

.card {
  &.is-fullheight {
    height: 100%;
  }
  &.has-bottom-button {
    .card-content {
      padding-bottom: 3rem;
      .button {
        position: absolute;
        bottom: 0;
        right: 0;
      }
    }
  }
}
</style>
